import { PrevNextButton } from '@/components/ace/subgroup/PrevNextButton'
import { getLinkedItems } from '@/components/contexts/KontentHomeConfigProvider'
import RichText from '@/components/RichText'
import { CommonCopyUrlWrapper } from '@/components/ui/copy-to-clipboard/CommonCopyUrlWrapper'
import { WrapperWithInView } from '@/components/WrapperWithInView'
import type { Mapping } from '@/types'
import type { IContentItemsContainer } from '@kontent-ai/delivery-sdk'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import { FormGroupSelect } from 'nsw-ds-react'
import type { FormOption } from 'nsw-ds-react/dist/component/forms'
import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ChangeEvent,
	type ReactNode,
	type RefObject,
} from 'react'
import PrescriptionGroup from './PrescriptionGroup'
import PrescriptionStudy from './PrescriptionStudy'
import type {
	PrescriptionGroup as PrescriptionGroupType,
	PrescriptionOption as PrescriptionOptionItem,
	PrescriptionStudy as PrescriptionStudyType,
} from './types'
import {
	getGAPageContext,
	PRESCRIPTION_GA_EVENT,
} from './utils/prescriptionGAEvents'
import { sendPrescriptionGAEvent } from './utils/sendPrescriptionGAEvent'

const TYPE_PRESCRIPTION_GROUP = 'prescription_group'
const TYPE_PRESCRIPTION_STUDY = 'prescription_study'

const URL_PARAM = 'prescriptionoption'

// page_query (search string without leading `?`) the URL WILL have once the
// option is applied. Computed directly from the codename because the GA event
// fires before `router.replace` (async, shallow) updates window.location.
const buildOptionPageQuery = (codename: string): string => {
	if (typeof window === 'undefined') return ''
	const url = new URL(window.location.href)
	url.searchParams.set(URL_PARAM, codename)
	return url.search.replace(/^\?/, '')
}

// Base fields shared across prescription-view GA events, provided by the parent
// section (snake_case to match the v2 GA payload).
export interface PrescriptionGAOptionBase {
	focus_area: string
	syllabus: string
	prescriptions_list: string
}

export type PrescriptionOptionVariantKind = 'electives' | 'author-study'

export interface PrescriptionOptionProps {
	options: PrescriptionOptionItem[]
	variantKind: PrescriptionOptionVariantKind
	chosenListCodename: string
	linkedItems: IContentItemsContainer
	mappings: Mapping[]
	sectionHeadingRef: RefObject<HTMLElement>
	getGAEventBase: () => PrescriptionGAOptionBase
	slotBeforePrevNext?: ReactNode
}

export default function PrescriptionOption({
	options,
	variantKind,
	chosenListCodename,
	linkedItems,
	mappings,
	sectionHeadingRef,
	getGAEventBase,
	slotBeforePrevNext,
}: PrescriptionOptionProps) {
	const router = useRouter()

	// Initial active index resolution from URL query param.
	// Reading directly from `router.query` rather than `window.location` so the
	// component is SSR-safe (router.query is empty on SSR; we re-sync on mount).
	const initialActiveIndex = useMemo(() => {
		if (variantKind === 'author-study') return 0

		const queryValue = router.query[URL_PARAM]
		const codename = Array.isArray(queryValue) ? queryValue[0] : queryValue
		if (!codename) return 0

		const matchedIndex = options.findIndex(
			(opt) => opt.system.codename === codename,
		)

		return matchedIndex >= 0 ? matchedIndex : 0
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const [activeIndex, setActiveIndex] = useState<number>(initialActiveIndex)

	const activeOption = options[activeIndex] ?? options[0]

	// GA E2 dispatcher — fires for every active-option change in Ext 1.
	const fireGAOptionChange = useCallback(
		(newOption: PrescriptionOptionItem, pageQuery: string) => {
			if (variantKind !== 'electives') return

			const base = getGAEventBase()
			sendPrescriptionGAEvent({
				event: PRESCRIPTION_GA_EVENT.optionsView,
				...getGAPageContext(),
				page_query: pageQuery,
				focus_area: base.focus_area,
				syllabus: base.syllabus,
				prescriptions_list: base.prescriptions_list,
				prescriptions_option: newOption.elements.title.value,
			})
		},
		[variantKind, getGAEventBase],
	)

	const hasFiredInitialOptionViewRef = useRef(false)
	useEffect(() => {
		if (variantKind !== 'electives') return
		if (hasFiredInitialOptionViewRef.current) return
		if (options.length === 0) return
		hasFiredInitialOptionViewRef.current = true
		// Initial load reflects the ACTUAL current query (no URL change here).
		fireGAOptionChange(options[activeIndex], getGAPageContext().page_query)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Updates the URL query param using the Q14 lookup pattern:
	// new URL → searchParams.set → router.replace shallow.
	// Preserves any other existing query params (AC-E05).
	const updateUrlForOption = useCallback(
		(newCodename: string) => {
			if (typeof window === 'undefined') return
			if (variantKind !== 'electives') return

			const url = new URL(window.location.href)
			url.searchParams.set(URL_PARAM, newCodename)
			const currentUrl = new URL(window.location.href)
			if (currentUrl.href !== url.href) {
				router.replace(url, undefined, {
					shallow: true,
					scroll: false,
				})
			}
		},
		[router, variantKind],
	)

	// Scroll the section heading to the top of the viewport.
	const scrollToHeading = useCallback(() => {
		sectionHeadingRef.current?.scrollIntoView({
			behavior: 'smooth',
			block: 'start',
		})
	}, [sectionHeadingRef])

	const scrollElementBelowSticky = useCallback(
		(target: HTMLElement | null) => {
			if (!target) {
				scrollToHeading()
				return
			}
			const headerHeight =
				sectionHeadingRef.current?.getBoundingClientRect().height ?? 0
			const dropdown = document.querySelector(
				'[data-testid="prescription-option-dropdown"]',
			) as HTMLElement | null
			const dropdownHeight = dropdown?.getBoundingClientRect().height ?? 0
			const offset = headerHeight + dropdownHeight + 16
			const top =
				window.scrollY + target.getBoundingClientRect().top - offset
			window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' })
		},
		[sectionHeadingRef, scrollToHeading],
	)

	const scrollToOptionAnchor = useCallback(
		(hash: string) => {
			if (typeof document === 'undefined') return
			let target: HTMLElement | null = null
			try {
				target = document.querySelector(hash)
			} catch {
				target = null
			}
			scrollElementBelowSticky(target)
		},
		[scrollElementBelowSticky],
	)

	const scrollToOptionHeader = useCallback(() => {
		if (typeof document === 'undefined') return
		const target = document.querySelector(
			'[data-testid="prescription-option-title"]',
		) as HTMLElement | null
		scrollElementBelowSticky(target)
	}, [scrollElementBelowSticky])

	const changeActiveOption = useCallback(
		(newIndex: number) => {
			const newOption = options[newIndex]
			if (!newOption) return
			setActiveIndex(newIndex)
			updateUrlForOption(newOption.system.codename)
			fireGAOptionChange(
				newOption,
				buildOptionPageQuery(newOption.system.codename),
			)
			if (typeof window !== 'undefined') {
				window.requestAnimationFrame(() => scrollToOptionHeader())
			} else {
				scrollToOptionHeader()
			}
		},
		[options, updateUrlForOption, fireGAOptionChange, scrollToOptionHeader],
	)

	// Prev/Next handlers (Ext 1 path).
	const handlePrev = useCallback(() => {
		changeActiveOption(activeIndex - 1)
	}, [activeIndex, changeActiveOption])

	const handleNext = useCallback(() => {
		changeActiveOption(activeIndex + 1)
	}, [activeIndex, changeActiveOption])

	const handleDropdownChange = useCallback(
		(e: ChangeEvent<HTMLSelectElement>) => {
			const newCodename = e.target.value
			const newIndex = options.findIndex(
				(opt) => opt.system.codename === newCodename,
			)
			if (newIndex >= 0) {
				changeActiveOption(newIndex)
			}
		},
		[options, changeActiveOption],
	)

	const hasSyncedFromUrlRef = useRef(false)
	useEffect(() => {
		if (variantKind !== 'electives') return
		if (!router.isReady) return
		if (hasSyncedFromUrlRef.current) return
		hasSyncedFromUrlRef.current = true

		const queryValue = router.query[URL_PARAM]
		const codename = Array.isArray(queryValue) ? queryValue[0] : queryValue
		if (!codename) return

		const matchedIndex = options.findIndex(
			(opt) => opt.system.codename === codename,
		)
		if (matchedIndex < 0) return

		setActiveIndex(matchedIndex)
		const hash = typeof window !== 'undefined' ? window.location.hash : ''
		if (hash) {
			const timer = window.setTimeout(
				() => scrollToOptionAnchor(hash),
				300,
			)
			return () => window.clearTimeout(timer)
		}
		const timer = window.setTimeout(() => scrollToHeading(), 0)
		return () => window.clearTimeout(timer)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [router.isReady])

	if (variantKind === 'author-study') {
		return (
			<>
				{options.map((option) => {
					const studies = (
						getLinkedItems(
							option.elements.prescriptions,
							linkedItems,
						) ?? []
					).filter(
						(item) => item.system.type === TYPE_PRESCRIPTION_STUDY,
					) as PrescriptionStudyType[]

					const optionAnchorId = `po-${option.system.id}`
					const optionCopyUrl =
						typeof window !== 'undefined'
							? `${window.location.pathname}#${optionAnchorId}`
							: `#${optionAnchorId}`

					return (
						<div
							key={option.system.id}
							data-testid="prescription-option"
							data-kontent-item-id={option.system.id}
						>
							{option.elements.title.value && (
								<CommonCopyUrlWrapper
									url={optionCopyUrl}
									className="nsw-h3 mt-6"
									id={optionAnchorId}
									data-testid="prescription-option-title"
									data-kontent-element-codename="title"
								>
									{option.elements.title.value}
								</CommonCopyUrlWrapper>
							)}
							<div
								data-testid="prescription-option-content"
								className="mt-6"
							>
								<RichText
									richTextElement={option.elements.content}
									linkedItems={linkedItems}
									mappings={mappings}
								/>
							</div>
							<div className="mt-8 space-y-8">
								{studies.map((study) => (
									<PrescriptionStudy
										key={study.system.id}
										study={study}
										linkedItems={linkedItems}
										mappings={mappings}
									/>
								))}
							</div>
						</div>
					)
				})}
			</>
		)
	}

	// ----- ELECTIVES (Ext 1) PATH -----
	const dropdownOptions = options.map<FormOption>((opt) => ({
		text: opt.elements.title.value,
		value: opt.system.codename,
	}))

	const groupsForActiveOption = (
		getLinkedItems(activeOption.elements.prescriptions, linkedItems) ?? []
	)
		.filter((item) => item.system.type === TYPE_PRESCRIPTION_GROUP)
		.filter((group) =>
			(
				getLinkedItems(
					(group as PrescriptionGroupType).elements.prescription_list,
					linkedItems,
				) ?? []
			).some((list) => list.system.codename === chosenListCodename),
		) as PrescriptionGroupType[]

	const isFirst = activeIndex === 0
	const isLast = activeIndex === options.length - 1
	const isSingle = options.length <= 1
	const showPrev = !isFirst && !isSingle
	const showNext = !isLast && !isSingle

	const activeOptionAnchorId = `po-${activeOption.system.id}`
	const activeOptionCopyUrl = (() => {
		if (typeof window === 'undefined') return ''
		const url = new URL(window.location.href)
		url.searchParams.set(URL_PARAM, activeOption.system.codename)
		return `${url.pathname}${url.search}`
	})()

	return (
		<div
			data-testid="prescription-option"
			data-kontent-item-id={activeOption.system.id}
		>
			<WrapperWithInView
				css={{
					'&.WrapperWithInView-trigger': {
						transform:
							'translateY(calc(var(--prescription-title-height, 0px) * -1))',
					},
				}}
			>
				{(inView) => (
					<div
						className={clsx(
							'sticky z-[2] border-t-2 border-b-2 border-white bg-white -mx-8 px-8 pb-2 !-mb-2 transition',
							!inView && 'border-b-black',
						)}
						css={{
							top: 'calc(var(--prescription-title-height, 0px) - 2px)',
							'.is-preview &': {
								top: 'calc(var(--prescription-title-height, 0px) + 26px - 2px)',
							},
						}}
					>
						<FormGroupSelect
							key={activeOption.system.codename}
							selected={activeOption.system.codename}
							className="border-nsw-blue-01 border-2 font-bold text-nsw-blue-01 truncate"
							label="Select prescription option"
							placeholder=""
							options={dropdownOptions}
							onChange={handleDropdownChange}
							hideLabel
							autoComplete="off"
							data-testid="prescription-option-dropdown"
						/>
					</div>
				)}
			</WrapperWithInView>

			{activeOption.elements.title.value && (
				<CommonCopyUrlWrapper
					url={activeOptionCopyUrl}
					className="nsw-h4 mb-8 pt-8"
					id={activeOptionAnchorId}
					data-testid="prescription-option-title"
					data-kontent-element-codename="title"
				>
					{activeOption.elements.title.value}
				</CommonCopyUrlWrapper>
			)}

			<div data-testid="prescription-option-content" className="mt-6">
				<RichText
					richTextElement={activeOption.elements.content}
					linkedItems={linkedItems}
					mappings={mappings}
				/>
			</div>

			<div className="mt-6 space-y-6">
				{groupsForActiveOption.map((group) => (
					<PrescriptionGroup
						key={group.system.id}
						group={group}
						linkedItems={linkedItems}
						mappings={mappings}
						optionCodename={activeOption.system.codename}
					/>
				))}
			</div>

			{slotBeforePrevNext}

			{(showPrev || showNext) && (
				<div className="mt-8 flex justify-between gap-6">
					{showPrev ? (
						<div data-testid="prescription-prev">
							<PrevNextButton
								className="[&_.Link]:text-nsw-brand-dark [&_.Link]:font-bold [&_.Link]:underline [&_.Link:hover]:bg-nsw-text-hover [&_.Link:focus]:outline-3 [&_.Link:focus]:outline [&_.Link:focus]:outline-[var(--nsw-focus)]"
								type="Previous"
								contentItem={{
									text: options[activeIndex - 1]?.elements
										.title.value,
									onClick: handlePrev,
								}}
							>
								<span>Previous</span>
							</PrevNextButton>
						</div>
					) : (
						<span />
					)}
					{showNext ? (
						<div data-testid="prescription-next">
							<PrevNextButton
								className="[&_.Link]:text-nsw-brand-dark [&_.Link]:font-bold [&_.Link]:underline [&_.Link:hover]:bg-nsw-text-hover [&_.Link:focus]:outline-3 [&_.Link:focus]:outline [&_.Link:focus]:outline-[var(--nsw-focus)]"
								type="Next"
								contentItem={{
									text: options[activeIndex + 1]?.elements
										.title.value,
									onClick: handleNext,
								}}
							>
								<span>Next</span>
							</PrevNextButton>
						</div>
					) : (
						<span />
					)}
				</div>
			)}
		</div>
	)
}
